import type { TimelineItem } from '@citibox-panel/ui';
import { DELIVERY_STATES, deliveryTimelineLabel } from '../../../lib/deliveryStates';
import { collectionAuthorshipNote } from './collectionAuthorshipNote';

// The fields of the «Entrega» detail the timeline is built from. The Zod-inferred detail
// domain object (a superset) satisfies this structurally.
export interface DeliveryTimelineInput {
  status: string;
  storedDate: string | null;
  collectedDate: string | null;
  blockedDate: string | null;
  rejectedDate: string | null;
  cancelledDate: string | null;
  failedDate: string | null;
  retrievedDate: string | null;
  nonCollectedDate: string | null;
  nonRetrievedDate: string | null;
  estimatedExpirationDate: string | null;
  isExtendedExpirationDate: boolean;
  // Whether the «Usuario» confirmed the pickup (CBX-11159). Read by the `collected` branch alone,
  // and `null` — «not attributable» — is its dominant value.
  collectedByUser: boolean | null;
}

// Canonical lifecycle rank. Dated rows sort chronologically; dateless rows (and ties)
// fall back to this order so the sequence stays sensible (delivered → collected →
// terminal → the pending future estimate).
const ORDER = { delivered: 0, collected: 1, terminal: 2, estimate: 3 } as const;

// Statuses for which a pickup is still pending, so the future «Expira la recogida» row
// applies — matched case-insensitively (the backend returns lowercase) over the canonical
// backend-confirmed spellings (underscores).
const PENDING_STATUSES = new Set<string>([DELIVERY_STATES.stored]);

// The input date field each terminal occurred state reads its transition instant from —
// CBX-10877 gave every lifecycle state its own `*_date`, so these rows are dated instead of
// falling through to a bare label. The row degrades to a dateless em-dash when the field is
// null (no backfill: only transitions after the backend deploy populate it). `expired` is
// deliberately absent — it has no own column (its instant is `estimated_expiration_date`) and
// carries a warning dot, so it stays a dedicated branch below.
type TerminalDateField =
  | 'blockedDate'
  | 'rejectedDate'
  | 'cancelledDate'
  | 'failedDate'
  | 'retrievedDate'
  | 'nonCollectedDate'
  | 'nonRetrievedDate';

const TERMINAL_DATE_FIELD: Record<string, TerminalDateField> = {
  [DELIVERY_STATES.blocked]: 'blockedDate',
  [DELIVERY_STATES.rejected]: 'rejectedDate',
  [DELIVERY_STATES.cancelled]: 'cancelledDate',
  [DELIVERY_STATES.failed]: 'failedDate',
  [DELIVERY_STATES.retrieved]: 'retrievedDate',
  [DELIVERY_STATES.nonCollected]: 'nonCollectedDate',
  [DELIVERY_STATES.nonRetrieved]: 'nonRetrievedDate',
};

interface Entry {
  item: TimelineItem;
  order: number;
}

// A single occurred row, labelled from `status` (via the shared state map, case-insensitive)
// and dated from `datetime` when present — a null `datetime` degrades to a dateless row the
// view renders as an em dash. `dotColor` tints the dot (expired only); `order` overrides the
// default terminal rank for the earlier «Recogido» row.
function occurredEntry(
  status: string,
  datetime: string | null,
  opts: { order?: number; dotColor?: 'warning' } = {},
): Entry {
  return {
    item: {
      label: deliveryTimelineLabel(status),
      variant: 'occurred',
      ...(datetime !== null ? { datetime } : {}),
      ...(opts.dotColor !== undefined ? { dotColor: opts.dotColor } : {}),
    },
    order: opts.order ?? ORDER.terminal,
  };
}

// The rows for the delivery's current state, on top of the «Entregado» row the stored date
// already produced. `stored` (pending) adds a future «Expira la recogida» row; `collected`
// its own «Recogido» row; `expired` an amber occurred row dated from the expiration instant;
// every other terminal state its own dated occurred row (TERMINAL_DATE_FIELD); an unknown
// status falls open to a dateless occurred row labelled with the raw string.
function currentStateEntries(input: DeliveryTimelineInput): Entry[] {
  const status = input.status.toLowerCase();

  if (PENDING_STATUSES.has(status)) {
    // TODO: i18n — copy en ES hasta Fase 1.
    return [
      {
        item: {
          label: 'Expira la recogida',
          variant: 'estimated',
          ...(input.estimatedExpirationDate !== null ? { datetime: input.estimatedExpirationDate } : {}),
          ...(input.isExtendedExpirationDate ? { badge: 'Ampliada' } : {}),
        },
        order: ORDER.estimate,
      },
    ];
  }

  // Collected: a single «Recogido» occurred row, dated from the pickup instant. Gated on the
  // status, NOT on collectedDate presence — a Customer Care restore keeps collected_date
  // (CBX-10877), so a re-opened parcel arrives stored/expired again and must never render as
  // collected. A collected delivery with no date degrades to the dateless row.
  if (status === DELIVERY_STATES.collected) {
    const entry = occurredEntry(DELIVERY_STATES.collected, input.collectedDate, {
      order: ORDER.collected,
    });
    // The authorship caption hangs off THIS row and no other. Merged into `entry.item` explicitly:
    // `occurredEntry` returns an `Entry` (`{item, order}`), so spreading the note at that level
    // would typecheck — TS does not flag excess properties introduced by a spread — and then render
    // nothing at all. The conditional spread (rather than `note: undefined`) is required by
    // `exactOptionalPropertyTypes`, and it is what makes «unknown authorship produces no key» true.
    const note = collectionAuthorshipNote(input.collectedByUser);
    return [{ ...entry, item: { ...entry.item, ...(note !== undefined ? { note } : {}) } }];
  }

  // Expired: occurred pickup-expiry with an amber dot, dated from the expiration instant
  // (estimated_expiration_date IS the date it expired — a backend signal, never date-vs-now).
  // A dateless expired still degrades to the em-dash row.
  if (status === DELIVERY_STATES.expired) {
    return [occurredEntry(DELIVERY_STATES.expired, input.estimatedExpirationDate, { dotColor: 'warning' })];
  }

  // The terminal occurred states that each carry their own transition date (CBX-10877):
  // rejected / cancelled / failed / retrieved / non_collected / non_retrieved / blocked.
  // Own-property only: `status` is wire data and a bare lookup also resolves inherited names.
  const dateField = Object.hasOwn(TERMINAL_DATE_FIELD, status) ? TERMINAL_DATE_FIELD[status] : undefined;
  if (dateField !== undefined) {
    return [occurredEntry(input.status, input[dateField])];
  }

  // Unknown status → fail open to a dateless occurred row labelled with the raw string.
  return [occurredEntry(input.status, null)];
}

// Build the trazabilidad rows for an «Entrega» from its recorded dates and current
// status. Timezone-agnostic: `datetime` carries the raw ISO instant for the view to
// localize (formatDeliveryDateTime), and expiry is read from backend signals, never by
// comparing a date to "now".
// A parcel with no delivery event (legacy, no storedDate) has no HISTORY, so every occurred row
// falls away and the caller shows "Sin trazabilidad disponible". The pending «Expira la recogida»
// estimate is the one exception, and it is deliberate: it describes a FUTURE, not a past event, so
// it stands on its own — and it is the anchor «Ampliar custodia» hangs its control from, so dropping
// it would silently drop the action on exactly the fixture the SPEC calls a first-class case (`BARE`
// — `stored` with no dates at all, and the fallback for an unknown id).
export function buildDeliveryTimeline(input: DeliveryTimelineInput): TimelineItem[] {
  const hasDeliveryEvent = input.storedDate !== null;
  const isPending = PENDING_STATUSES.has(input.status.toLowerCase());
  if (!hasDeliveryEvent && !isPending) return [];

  const entries: Entry[] = [
    // TODO: i18n — copy en ES hasta Fase 1.
    ...(input.storedDate !== null
      ? [
          {
            item: {
              label: deliveryTimelineLabel(DELIVERY_STATES.stored),
              datetime: input.storedDate,
              variant: 'occurred' as const,
            },
            order: ORDER.delivered,
          },
        ]
      : []),
    ...currentStateEntries(input),
  ];

  entries.sort((a, b) => {
    const da = a.item.datetime;
    const db = b.item.datetime;
    if (da !== undefined && db !== undefined && da !== db) return da < db ? -1 : 1;
    return a.order - b.order;
  });

  return entries.map((entry) => entry.item);
}
